import { useCallback, useEffect, useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme';
const DARK_MQ = '(prefers-color-scheme: dark)';

// ---------------------------------------------------------------------------
// Tiny external store so every consumer shares the same reactive state
// without needing a React context.
// ---------------------------------------------------------------------------
function readInitialTheme(): ThemeMode {
  if (typeof localStorage === 'undefined') return 'system';
  const stored = localStorage.getItem(STORAGE_KEY);
  return (stored === 'light' || stored === 'dark' || stored === 'system') ? stored : 'system';
}

let currentTheme: ThemeMode = readInitialTheme();
const listeners = new Set<() => void>();

function getSnapshot(): ThemeMode {
  return currentTheme;
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

function notify(): void {
  listeners.forEach((l) => l());
}

// ---------------------------------------------------------------------------
// Apply the resolved dark/light class to <html> AND body.dataset.mode.
// body.dataset.mode is REQUIRED so the shared ChatWidget's dark-mode cascade
// works via `[data-mode="dark"] .nerdy-panel...` selectors (FR-034 resolution).
// ---------------------------------------------------------------------------
function resolveIsDark(theme: ThemeMode): boolean {
  if (typeof window === 'undefined') return false;
  const prefersDark = window.matchMedia(DARK_MQ).matches;
  return theme === 'dark' || (theme === 'system' && prefersDark);
}

function applyTheme(theme: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const isDark = resolveIsDark(theme);
  document.documentElement.classList.toggle('dark', isDark);
  document.body.dataset.mode = isDark ? 'dark' : 'light';
}

// Apply immediately so there is no flash of wrong theme on page load.
if (typeof document !== 'undefined') {
  applyTheme(currentTheme);
}

// Listen for OS preference changes (always active so the class stays correct
// even if no component is mounted yet).
if (typeof window !== 'undefined') {
  window.matchMedia(DARK_MQ).addEventListener('change', () => {
    if (currentTheme === 'system') {
      applyTheme('system');
      notify();
    }
  });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export interface UseThemeReturn {
  /** The stored preference: 'light' | 'dark' | 'system' */
  theme: ThemeMode;
  /** Update the preference and persist to localStorage */
  setTheme: (t: ThemeMode) => void;
  /** Whether the resolved appearance is currently dark */
  isDark: boolean;
}

export function useTheme(): UseThemeReturn {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Re-apply whenever the theme value changes (covers system media-query
  // changes as well because those also call notify()).
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemeMode) => {
    currentTheme = next;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, next);
    }
    applyTheme(next);
    notify();
  }, []);

  return { theme, setTheme, isDark: resolveIsDark(theme) };
}
